import { CircularProgress, Typography } from '@mui/material';
import {
    FunctionComponent,
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';

import {
    getPlanningSession,
    submitPlanningAnswers
} from '../../../../services/api';
import {
    PlanningSessionData,
    PlanningSessionStatus,
    WorkItem
} from '../../../../types/work';
import PlanningQuestionsForm, {
    PlanningAnswerPayload
} from './PlanningQuestionsForm';
import PlanningSpinner from './PlanningSpinner';
import {
    ErrorBox,
    errorText,
    PlanReadyActionsRow,
    PlanReadyContent,
    PlanReadyMessage,
    PlanReadyRoot,
    PrimaryActionButton,
    submitProgressSx
} from './styled';

interface Props {
    work: WorkItem;
    onSubmitWork: () => void;
    isSubmittingWork?: boolean;
}

const POLL_INTERVAL_MS = 3000;

const PlanningQuestionsAnswering: FunctionComponent<Props> = ({
    work,
    onSubmitWork,
    isSubmittingWork
}) => {
    const sessionId = String(work.workInput?.planning_session_id ?? '');
    const [ session, setSession ] = useState<PlanningSessionData | null>(null);
    const [ error, setError ] = useState<string | null>(null);
    const [ isSubmittingAnswers, setIsSubmittingAnswers ] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchSession = useCallback(async () => {
        if (!sessionId) {
            return;
        }
        try {
            const data = (await getPlanningSession(
                sessionId
            )) as PlanningSessionData | undefined;
            if (data?.sessionId) {
                setSession(data);
                setError(null);
            } else {
                setError('Failed to load planning session');
            }
        } catch {
            setError('Failed to load planning session');
        }
    }, [ sessionId ]);

    useEffect(() => {
        void fetchSession();
    }, [ fetchSession ]);

    useEffect(() => {
        if (session?.status === PlanningSessionStatus.PROCESSING) {
            pollRef.current = setInterval(() => {
                void fetchSession();
            }, POLL_INTERVAL_MS);
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
            }
            pollRef.current = null;
        };
    }, [ session?.status, fetchSession ]);

    const handleSubmitAnswers = useCallback(
        async (formatted: PlanningAnswerPayload[]) => {
            if (!sessionId) {
                return;
            }
            setIsSubmittingAnswers(true);
            try {
                const data = (await submitPlanningAnswers(
                    sessionId,
                    formatted
                )) as PlanningSessionData | undefined;
                if (data?.sessionId) {
                    setSession(data);
                    setError(null);
                } else {
                    setError('Failed to submit answers');
                }
            } catch {
                setError('Failed to submit answers');
            } finally {
                setIsSubmittingAnswers(false);
            }
        },
        [ sessionId ]
    );

    if (!sessionId) {
        return (
            <ErrorBox>
                <Typography sx={errorText}>
                    This work item is missing its planning session id.
                </Typography>
            </ErrorBox>
        );
    }

    if (error) {
        return (
            <ErrorBox>
                <Typography sx={errorText}>{error}</Typography>
            </ErrorBox>
        );
    }

    if (!session) {
        return <PlanningSpinner text="Loading planning session..." />;
    }

    if (session.status === PlanningSessionStatus.PROCESSING) {
        return <PlanningSpinner />;
    }

    if (session.status === PlanningSessionStatus.PLAN_READY) {
        return (
            <PlanReadyRoot>
                <PlanReadyContent>
                    <PlanReadyMessage>
                        The plan is ready. Please submit this work item.
                    </PlanReadyMessage>
                    <PlanReadyActionsRow>
                        <PrimaryActionButton
                            variant="contained"
                            onClick={onSubmitWork}
                            disabled={isSubmittingWork}
                        >
                            {isSubmittingWork ? (
                                <CircularProgress
                                    size={20}
                                    sx={submitProgressSx}
                                />
                            ) : (
                                'Submit'
                            )}
                        </PrimaryActionButton>
                    </PlanReadyActionsRow>
                </PlanReadyContent>
            </PlanReadyRoot>
        );
    }

    if (
        session.status === PlanningSessionStatus.AWAITING_ANSWERS &&
        session.questions &&
        session.questions.length > 0
    ) {
        return (
            <PlanningQuestionsForm
                questions={session.questions}
                onSubmit={handleSubmitAnswers}
                isSubmitting={isSubmittingAnswers}
            />
        );
    }

    return (
        <ErrorBox>
            <Typography sx={errorText}>
                Planning session is in an unexpected state ({session.status}).
            </Typography>
        </ErrorBox>
    );
};

export default PlanningQuestionsAnswering;
